/**
 * Widget Grid Components
 *
 * Replacement for Bootstrap's grid system using eg-* prefixed classes.
 * These components don't depend on Bootstrap CSS.
 */

import React from 'react';

/**
 * Container component - replacement for Bootstrap Container
 */
export const EgContainer = ({ children, fluid = false, className = '', ...props }) => {
  const containerClass = fluid ? 'eg-container-fluid' : 'eg-container';
  return (
    <div className={`${containerClass} ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Row component - replacement for Bootstrap Row
 */
export const EgRow = ({ children, className = '', ...props }) => {
  return (
    <div className={`eg-row ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Col component - replacement for Bootstrap Col
 *
 * @param {number} xs - Extra small breakpoint (0px+)
 * @param {number} sm - Small breakpoint (576px+)
 * @param {number} md - Medium breakpoint (768px+)
 * @param {number} lg - Large breakpoint (992px+)
 * @param {number} xl - Extra large breakpoint (1200px+)
 */
export const EgCol = ({
  children,
  xs,
  sm,
  md,
  lg,
  xl,
  className = '',
  ...props
}) => {
  const classes = ['eg-col'];

  if (xs) classes.push(`eg-col-${xs}`);
  if (sm) classes.push(`eg-col-sm-${sm}`);
  if (md) classes.push(`eg-col-md-${md}`);
  if (lg) classes.push(`eg-col-lg-${lg}`);
  if (xl) classes.push(`eg-col-xl-${xl}`);

  // If no size specified, use full width
  if (!xs && !sm && !md && !lg && !xl) {
    classes.push('eg-col-12');
  }

  return (
    <div className={`${classes.join(' ')} ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Button component - replacement for Bootstrap Button
 */
export const EgButton = ({
  children,
  variant = 'primary',
  size = 'md',
  outline = false,
  disabled = false,
  className = '',
  onClick,
  type = 'button',
  style,
  ...props
}) => {
  const classes = [
    'eg-btn',
    `eg-btn-${outline ? 'outline-' : ''}${variant}`,
    size !== 'md' ? `eg-btn-${size}` : ''
  ].filter(Boolean);

  return (
    <button
      type={type}
      className={`${classes.join(' ')} ${className}`}
      disabled={disabled}
      onClick={onClick}
      style={style}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Simple Tab system - replacement for Bootstrap Tabs
 */
export const EgTabs = ({ children, activeKey, onSelect, className = '' }) => {
  return (
    <div className={`eg-tabs ${className}`}>
      {React.Children.map(children, child => {
        if (!child) return null;
        return React.cloneElement(child, { activeKey, onSelect });
      })}
    </div>
  );
};

export const EgTabList = ({ children, className = '' }) => {
  return (
    <div className={`eg-tab-list ${className}`}>
      {children}
    </div>
  );
};

export const EgTab = ({ children, eventKey, activeKey, onSelect, className = '' }) => {
  const isActive = activeKey === eventKey;
  return (
    <button
      className={`eg-tab ${isActive ? 'eg-tab-active' : ''} ${className}`}
      onClick={() => onSelect && onSelect(eventKey)}
      type="button"
    >
      {children}
    </button>
  );
};

export const EgTabPanels = ({ children, activeKey, className = '' }) => {
  return (
    <div className={`eg-tab-panels ${className}`}>
      {React.Children.map(children, child => {
        if (!child) return null;
        return React.cloneElement(child, { activeKey });
      })}
    </div>
  );
};

export const EgTabPanel = ({ children, eventKey, activeKey, className = '' }) => {
  const isActive = activeKey === eventKey;
  if (!isActive) return null;

  return (
    <div className={`eg-tab-panel ${className}`}>
      {children}
    </div>
  );
};

/**
 * Simple Stepper component - replacement for CDBStepper
 */
export const EgStepper = ({ steps, activeStep, onStepClick, className = '' }) => {
  return (
    <div className={`eg-stepper ${className}`}>
      {steps.map((step, index) => (
        <div
          key={index}
          className={`eg-step ${index === activeStep ? 'eg-step-active' : ''} ${index < activeStep ? 'eg-step-completed' : ''}`}
          onClick={() => onStepClick && onStepClick(index)}
        >
          <div className="eg-step-indicator">
            <span className="eg-step-number">{index + 1}</span>
          </div>
          {step.title && (
            <div className="eg-step-title">{step.title}</div>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Modal component - replacement for Bootstrap Modal
 */
export const EgModal = ({
  show,
  onHide,
  title,
  children,
  className = ''
}) => {
  if (!show) return null;

  return (
    <div className={`eg-modal-overlay ${className}`} onClick={onHide}>
      <div className="eg-modal" onClick={e => e.stopPropagation()}>
        <div className="eg-modal-header">
          <h5 className="eg-modal-title">{title}</h5>
          <button className="eg-modal-close" onClick={onHide}>&times;</button>
        </div>
        <div className="eg-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default {
  EgContainer,
  EgRow,
  EgCol,
  EgButton,
  EgTabs,
  EgTabList,
  EgTab,
  EgTabPanels,
  EgTabPanel,
  EgStepper,
  EgModal
};
